<template>
  <div class="section" id="repayment-type-selection">
    <h1 class="title">What type of repayment works best for you?</h1>
    <p class="sc-desc">
      There are {{ repaymentTerm?.numberOfRepaymentTypes }} repayment options to select from. Choose
      a repayment type to learn more and see estimated monthly payments. You will be able to review
      and edit your selections on the next page.
    </p>
    <div
      class="buttons"
      id="repayment-type-buttons"
      v-if="repaymentTerm && repaymentTypeOptions?.length > 0"
    >
      <button
        v-for="repaymentTypeOption in repaymentTypeOptions"
        :key="repaymentTypeOption?.repaymentTypeId || ''"
        class="repayment-type-button"
        :class="[{ active: repaymentTypeOption.repaymentTypeId === selectedRepaymentTypeId }]"
        @click="selectRepaymentType(repaymentTypeOption.repaymentTypeId || 0)"
      >
        <span class="button-text">{{
          repaymentTypeOption?.repaymentTypeDetails?.name || repaymentTypeOption.repaymentTypeName
        }}</span>
      </button>
    </div>
    <div class="sections" :class="{ hasSelection: selectedRepaymentTypeId > 0 }">
      <div class="section" id="repayment-type-description">
        <h5>{{ selectedRepaymentType?.repaymentTypeName || "Repayment Type" }}</h5>
        <p>
          {{
            selectedRepaymentType
              ? selectedRepaymentType?.repaymentTypeDetails?.description
              : "Select a repayment type to learn more."
          }}
        </p>
      </div>
      <div class="section" id="estimated-payments">
        <h5>Estimated Monthly Payments</h5>
        <div class="payments">
          <div class="payment">
            <p>{{ monthlyInSchoolPaymentLabel }}</p>
            <p>{{ selectedRepaymentType?.monthlyInSchoolPaymentDisplay || "—" }}</p>
          </div>
          <div class="payment">
            <p>{{ monthlyPostSchoolPaymentLabel }}</p>
            <p>{{ selectedRepaymentType?.monthlyPostSchoolPaymentDisplay || "—" }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useStore } from "vuex";
import type {
  RatePlanVM,
  RepaymentTermOption,
  RepaymentTypeOption,
} from "@/models/loanConfiguration";

const store = useStore();

const props = defineProps<{
  repaymentTerm?: RepaymentTermOption;
}>();

const selectedRepaymentTypeId = ref<number>(0);

const repaymentTypeOptions = computed<RepaymentTypeOption[]>(
  () => props.repaymentTerm?.repaymentTypeOptions ?? [],
);

const isValidRepaymentType = computed<boolean>(() => selectedRepaymentType.value !== undefined);

const selectedRepaymentType = computed<RepaymentTypeOption | undefined>(() => {
  if (repaymentTypeOptions.value) {
    return repaymentTypeOptions.value?.find(
      (r: RepaymentTypeOption) => r.repaymentTypeId === selectedRepaymentTypeId.value,
    );
  }

  return undefined;
});

const selectedRatePlan = computed<RatePlanVM | undefined>(() => {
  if (props.repaymentTerm) {
    return props.repaymentTerm.ratePlans.find(
      (r: RatePlanVM) => r.id === selectedRepaymentType.value?.ratePlanId,
    );
  }
  return undefined;
});

const monthlyInSchoolPaymentLabel = computed<string>(
  () => store.state.loanConfigurationModule?.loanOptionsConfiguration?.monthlyInSchoolPaymentLabel,
);

const monthlyPostSchoolPaymentLabel = computed<string>(
  () =>
    store.state.loanConfigurationModule?.loanOptionsConfiguration?.monthlyPostSchoolPaymentLabel,
);

const ratePlanRepaymentTypes = computed(() => {
  return selectedRatePlan.value?.repayments;
});

function selectRepaymentType(repaymentTypeId: number) {
  selectedRepaymentTypeId.value = repaymentTypeId;
}

function resetSelectedRepaymentType() {
  selectedRepaymentTypeId.value = 0;
}

defineExpose({
	isValidRepaymentType,
  monthlyInSchoolPaymentLabel,
  monthlyPostSchoolPaymentLabel,
  props,
  ratePlanRepaymentTypes,
  resetSelectedRepaymentType,
  repaymentTypeOptions,
  selectedRatePlan,
  selectedRepaymentTypeId,
  selectedRepaymentType,
  selectRepaymentType,
});
</script>

<style scoped src="@/styles/professional-grad/loanConfiguration.css"></style>
<style scoped src="@/styles/professional-grad/button.css"></style>
<style scoped src="@/styles/professional-grad/substep-4.css"></style>
